import { DateTime, Duration } from 'luxon';
import {
  Dispatch,
  Order,
  OrderAdditionalFee,
  OrderDocumentType,
  OrderInternalNote,
  OrderMedia,
  OrderMediaType,
  OrderPaymentMethod,
  OrderProduct,
  OrderProductAdditionalFee,
  OrderProductOption,
  OrderProductService,
  OrderReturnStatus,
  OrderSource,
  OrderStatus,
  OrderStatusHistory,
  OrderType,
} from './order';
import { ProductParsers } from '../product/product-parsers';
import { PaymentParsers } from '../payment/payment-parsers';
import { InvoiceParsers } from '../invoice/invoice-parsers';
import { User, UserAddress } from '../user/user';
import { AdminParsers } from '../admin/admin-parsers';
import { CustomOrderPriceRequest } from '../pricing/pricing';
import { UserParsers } from '../user/user-parsers';
import { Admin } from '../admin/admin';
import { ShippingType } from '../delivery/delivery';
import { ProductTrackingType } from '../product/product';
import { RepresentativeParsers } from '../representatives/representative-parsers';
import { TaxBreakdownJson, toTaxBreakdown } from '../tax/tax';
import { DispatchProgress, ItemDispatchProgress } from '../dispatch/dispatch';
import { parseIsoDate, parseOptionalIsoDate } from '../../../util/util';

export type OrderTotalsLegacyRequest = {
  selected_billing_address?: string;
  selected_delivery_address?: string;
  delay_edition: string;
  order_status: string;
  ua_address1: string;
  order_shipping_address_id: string;
  order_billing_address_id: string;
  order_id: string;
  user_id: string;
  location_id: string;
  colspan_plus?: string;
  products: {
    new_product_id: string;
    order_prod_id: string;
    new_product_start: string;
    new_product_end: string;
    new_product_type: string;
    new_prod_unique_id: string;
    new_product_delivery_method: string;
    new_assign_serial: string[];
    new_prod_editable_price: string;
    new_prod_editable_disc: string;
    new_product_qty: string;
    editable_fees?: { id: string; value: string }[];
    custom_order_rental_rates?: CustomOrderPriceRequest[];
  }[];
  delivery_charges?: string;
  coupon_code: string;
  editable_tax_percentage?: string;
  editable_tax?: string;
  editable_damage_waiver?: string;
  additional_discount?: string;
  editable_additional_fees?: { id: string; value: string }[];
  is_cycle_billed: string;
  cycle_length?: string;
  is_auto_charged: string;
};

export type OrderTotalsLegacyResponse = {
  OrderTotal: number;
  order: {
    order_add_discount: number;
    user_charge_tax: boolean;
    user_req_damage_waiver: boolean;
    order_discount_coupon: string;
    order_discount_total: number;
    order_cart_total: number;
    order_cart_total_without_tax: number;
    order_shipping_charged: number;
    order_tax_charged: number;
    order_sub_total: number;
    order_net_charged: number;
    order_actual_paid: number;
    order_currency_code: string;
    order_rental_security: number;
    order_damage_waiver: number;
    order_apply_damage_waiver_every_cicle: string;
    order_apply_damage_waiver_tax: string;
    order_location_id: string;
    addational_location_fess: number;
    order_tax_percentage_updated_at: string;
    order_tax_perc: number;
    order_tax_updated_at: string;
    order_shipping_updated_at: string;
    order_damage_waiver_updated_at: string;
    prods: {
      opr_product_id: string;
      opr_product_type: string;
      opr_qty: string;
      opr_name: string;
      opr_sku: string;
      opr_product_shop: string;
      opr_customer_buying_price: number;
      opr_tax_free: string;
      opr_shipping_type: number;
      opr_shipping_charges: number;
      opr_tax: number;
      opr_qty_tax: number;
      opr_net_charged: number;
      opr_sold_or_rented: number;
      opr_rental_start_date: string;
      opr_rental_end_date: string;
      opr_rental_security: number;
      opr_rental_price_multiplier: number;
      opr_buffer_period_before_rental: string;
      opr_buffer_period_after_rental: string;
      new_prod_unique_id: string;
      opr_discount_percentage: number;
      is_addon: boolean;
      opr_id: number;
      product_additional_fees: {
        topaf_id: string;
        topaf_order_id: string;
        topaf_product_id: number;
        topaf_opr_id: string;
        topaf_attribute_id: string;
        topaf_fee_title: string;
        topaf_fee_type: string;
        topaf_fee_value: string;
        topaf_cap_value: string;
        topaf_fee_applied_value: number;
        topaf_quantity: string;
        topaf_tax_appl: string;
        topaf_attribute_optional: number;
        topaf_updated_at: string;
        new_prod_unique_id: string;
      }[];
    }[];
    order_user_id: string;
    order_user_name: string;
    order_user_email: string;
    order_user_phone: string;
    order_shipping_name: string;
    order_shipping_company_name: string;
    order_shipping_address_id: string;
    order_shipping_address1: string;
    order_shipping_address2: string;
    order_shipping_city: string;
    order_shipping_postcode: string;
    order_shipping_state: null;
    order_shipping_country: string;
    order_shipping_phone: string;
    order_billing_name: string;
    order_billing_company_name: string;
    order_billing_address_id: string;
    order_billing_address1: string;
    order_billing_address2: string;
    order_billing_city: string;
    order_billing_postcode: string;
    order_billing_state: string;
    order_billing_country: string;
    order_billing_phone: string;
    order_apply_shipping_tax: string;
    cart_custom_fees: {
      toaf_id: string;
      toaf_order_id: string;
      toaf_fee_title: string;
      toaf_fee_type: string;
      toaf_fee_value: string;
      toaf_cap_value: string;
      toaf_fee_applied_value: number;
      toaf_recurring: string;
      toaf_tax_appl: string;
      toaf_optional: string;
      toaf_attribute_id: string;
      attribute_applicable_methods: string;
      toaf_updated_at: string;
      toaf_apply_every_cycle: string;
    }[];
    order_type: number;
    cart_product_additional_fees: {
      topaf_id: string;
      topaf_order_id: string;
      topaf_product_id: number;
      topaf_opr_id: string;
      topaf_attribute_id: string;
      topaf_fee_title: string;
      topaf_fee_type: string;
      topaf_fee_value: string;
      topaf_cap_value: string;
      topaf_fee_applied_value: number;
      topaf_quantity: string;
      topaf_tax_appl: string;
      topaf_attribute_optional: string;
      topaf_updated_at: string;
      new_prod_unique_id: string;
      topaf_apply_every_cycle: string;
    }[];
    order_security_deposit_percentage: number;
    coupon_prods: Record<string, { discount: number; tax_free: string }>;
  };
};

export type ProductInfoLegacyRequest = {
  order_id: string;
  product_id: string;
  rental_start_date: string;
  rental_start_time: string;
  rental_end_date?: string;
  rental_end_time?: string;
  shop_id: string;
  product_for: string;
  custom_order_rental_rates?: CustomOrderPriceRequest[];
};

export type ProductInfoLegacyResponse = {
  new_prod_price: number;
  new_prod_start_date: string;
  new_prod_end_date: string;
  prod_tracking_type: string;
  product_additional_fees: {
    attribute_id: string;
    attribute_title: string;
    attribute_description: string;
  }[];
};

export type OrderBufferPeriodLegacyRequest = {
  order_id: string;
  order_product_buffer: {
    opr_id: string;
    buffer_period_before_rental_date: string;
    buffer_period_before_rental_time: string;
    buffer_period_after_rental_date?: string;
    buffer_period_after_rental_time?: string;
    product_start_rental_time: string;
    product_end_rental_time?: string;
    prod_tracking_type: number;
    opr_product_id: string;
    opr_order_id: string;
    qty: number;
  }[];
};

export namespace OrderParsers {
  import OrderPaymentJson = PaymentParsers.OrderPaymentJson;
  import PaymentMethodJson = PaymentParsers.PaymentMethodJson;

  export class OrderProductJson {
    id!: number;
    orderId!: number;
    name!: string;
    isTaxFree!: boolean;
    tax!: number;
    product!: ProductParsers.ProductJson;
    assignedUnits!: ProductParsers.ProductInventoryUnitJson[];
    status?: OrderReturnStatus;
    type!: OrderType;
    quantity!: number;
    rentalStartDate!: string;
    rentalEndDate?: string;
    actualRentalStartDate?: string;
    actualRentalEndDate?: string;
    bufferMinutesBeforeRental?: number;
    bufferMinutesAfterRental?: number;
    trackingType?: ProductTrackingType;
    shippingTypeId?: number;
    shippingType!: ShippingType;
    additionalFees!: OrderProductAdditionalFeeJson[];
    customerBuyingPrice?: number;
    discountPercentage?: number;
    options!: OrderProductOption[];
    services!: OrderProductService[];
    inventoryReturnedAt?: string;
    deliveryDriverId?: number;
    pickupDriverId?: number;
    description?: string;
    generalLedgerAccountId?: string;
    taxCategoryId?: string;
    equipmentOwner?: string;
    deliveryStatus?: ItemDispatchProgress;
    pickupStatus?: ItemDispatchProgress;
  }

  export function toOrderProduct(input: OrderProductJson, timezone?: string): OrderProduct {
    return {
      id: input.id,
      orderId: input.orderId,
      name: input.name,
      isTaxFree: input.isTaxFree,
      tax: input.tax,
      product: ProductParsers.toProduct(input.product),
      assignedUnits: input.assignedUnits.map(ProductParsers.toProductInventoryUnit),
      status: input.status,
      type: input.type,
      quantity: input.quantity,
      trackingType: input.trackingType,
      rentalStartDate: parseIsoDate(input.rentalStartDate, timezone),
      rentalEndDate: parseOptionalIsoDate(input.rentalEndDate, timezone),
      actualRentalStartDate: parseOptionalIsoDate(input.actualRentalStartDate, timezone),
      actualRentalEndDate: parseOptionalIsoDate(input.actualRentalEndDate, timezone),
      bufferMinutesBeforeRental: input.bufferMinutesBeforeRental,
      bufferMinutesAfterRental: input.bufferMinutesAfterRental,
      shippingTypeId: input.shippingTypeId,
      shippingType: input.shippingType,
      additionalFees: input.additionalFees.map(toOrderProductFees),
      discountPercentage: input.discountPercentage || 0,
      customerBuyingPrice: input.customerBuyingPrice || 0,
      options: input.options,
      services: input.services,
      inventoryReturnedAt: parseOptionalIsoDate(input.inventoryReturnedAt, timezone),
      description: input.description,
      generalLedgerAccountId: input.generalLedgerAccountId,
      taxCategoryId: input.taxCategoryId,
      deliveryDriverId: input.deliveryDriverId,
      pickupDriverId: input.pickupDriverId,
      equipmentOwner: input.equipmentOwner,
      deliveryStatus: input.deliveryStatus,
      pickupStatus: input.pickupStatus,
    };
  }

  export class OrderJson {
    id!: number;
    locationId!: number;
    timezone?: string;
    orderNumber!: string;
    status!: OrderStatus;
    quoteExpiresAt?: string;
    returnStatus!: OrderReturnStatus;
    type!: OrderType;
    isDeleted!: boolean;
    products!: OrderProductJson[];
    user!: UserParsers.UserJson;
    pickupCharged!: number;
    pickupUpdatedAt?: string;
    shippingCharged?: number;
    shippingUpdatedAt?: string;
    durationDiscount?: number;
    discountTotal?: number;
    damageWaiver?: number;
    damageWaiverUpdatedAt?: string;
    applyDamageWaiverTax!: boolean;
    damageWaiverRecurring!: boolean;
    additionalDiscount?: number;
    billingAddress?: UserAddress;
    shippingAddress?: UserAddress;
    taxPercentage?: number;
    taxRate?: string;
    dateAdded!: string;
    source!: OrderSource;
    netCharged!: number;
    payments!: OrderPaymentJson[];
    notes!: string;
    cancellationReason?: string;
    cancellationMessage?: string;
    cancellationDate?: string;
    paymentMethod!: PaymentMethodJson;
    paymentMethodType!: OrderPaymentMethod;
    paymentType?: string;
    paymentLastFourDigits?: string;
    taxCharged!: number;
    taxUpdatedAt?: string;
    taxPercentageUpdatedAt?: string;
    poNumber?: string;
    discountCoupon?: string;
    cartTotal!: number;
    additionalFees!: OrderAdditionalFeeJson[];
    internalNotes!: OrderInternalNotesJson[];
    signature?: string;
    signatureDate?: string;
    invoices!: InvoiceParsers.InvoiceJson[];
    isPaid?: boolean;
    rentalSecurityPercentage?: number;
    rentalSecurity!: number;
    isCycleBilled?: boolean;
    isAutoCharged?: boolean;
    cycleBillingTerms?: {
      cycleLength: string;
      paymentPeriod: string;
      cycleInvoiceDay: number;
      isDayOfMonth: boolean;
    };
    adminId?: number;
    admin?: AdminParsers.AdminJson;
    statusHistory!: OrderStatusHistoryJson[];
    googleShippingCalculation?: {
      type: 'Distance' | 'Duration';
      distance: number;
      duration: string;
    };
    returnDate?: string;
    authorizedRepresentative?: RepresentativeParsers.RepresentativeJson;
    defaultInvoiceFreeText?: string;
    taxCode?: string;
    rentalDisclaimer?: string;
    salesRep?: AdminParsers.AdminJson;
    documentType!: OrderDocumentType;
    taxBreakdown?: TaxBreakdownJson[];
    dispatches?: DispatchJson[];
  }

  export function toOrder(input: OrderJson): Order {
    return {
      id: input.id,
      locationId: input.locationId,
      timezone: input.timezone,
      orderNumber: input.orderNumber,
      status: input.status,
      quoteExpiresAt: input.quoteExpiresAt ? DateTime.fromISO(input.quoteExpiresAt, { setZone: true }) : undefined,
      returnStatus: input.returnStatus,
      type: input.type,
      isDeleted: input.isDeleted,
      products: input.products.map((p) => toOrderProduct(p, input.timezone)),
      user: UserParsers.toUser(input.user),
      pickupCharged: input.pickupCharged,
      pickupUpdatedAt: input.pickupUpdatedAt ? DateTime.fromISO(input.pickupUpdatedAt, { setZone: true }) : undefined,
      shippingCharged: input.shippingCharged,
      shippingUpdatedAt: input.shippingUpdatedAt
        ? DateTime.fromISO(input.shippingUpdatedAt, { setZone: true })
        : undefined,
      durationDiscount: input.durationDiscount,
      discountTotal: input.discountTotal,
      damageWaiver: input.damageWaiver,
      applyDamageWaiverTax: input.applyDamageWaiverTax,
      damageWaiverRecurring: input.damageWaiverRecurring,
      damageWaiverUpdatedAt: input.damageWaiverUpdatedAt ? DateTime.fromISO(input.damageWaiverUpdatedAt) : undefined,
      additionalDiscount: input.additionalDiscount,
      billingAddress: input.billingAddress,
      shippingAddress: input.shippingAddress,
      dateAdded: DateTime.fromISO(input.dateAdded, { setZone: true }),
      source: input.source,
      netCharged: input.netCharged,
      payments: input.payments.map(PaymentParsers.toOrderPayment),
      notes: input.notes,
      cancellationReason: input.cancellationReason,
      cancellationMessage: input.cancellationMessage,
      cancellationDate: input.cancellationDate
        ? DateTime.fromISO(input.cancellationDate, { setZone: true })
        : undefined,
      paymentMethod: PaymentParsers.toPaymentMethod(input.paymentMethod),
      paymentMethodType: input.paymentMethodType,
      paymentType: input.paymentType,
      paymentLastFourDigits: input.paymentLastFourDigits,
      taxCharged: input.taxCharged,
      taxPercentage: input.taxPercentage,
      taxUpdatedAt: input.taxUpdatedAt ? DateTime.fromISO(input.taxUpdatedAt, { setZone: true }) : undefined,
      taxPercentageUpdatedAt: input.taxPercentageUpdatedAt
        ? DateTime.fromISO(input.taxPercentageUpdatedAt, { setZone: true })
        : undefined,
      taxCode: input.taxCode,
      poNumber: input.poNumber,
      discountCoupon: input.discountCoupon,
      cartTotal: input.cartTotal,
      additionalFees: input.additionalFees.map(toOrderAdditionalFees),
      internalNotes: input.internalNotes.map(toOrderInternalNotes),
      signature: input.signature,
      signatureDate: input.signatureDate ? DateTime.fromISO(input.signatureDate, { setZone: true }) : undefined,
      invoices: input.invoices.map(InvoiceParsers.toInvoice),
      isPaid: input.isPaid || false,
      rentalSecurityPercentage: input.rentalSecurityPercentage || 0,
      rentalSecurity: input.rentalSecurity || 0,
      isCycleBilled: input.isCycleBilled || false,
      isAutoCharged: input.isAutoCharged || false,
      cycleBillingTerms: input.cycleBillingTerms
        ? {
            cycleLength: Duration.fromISO(input.cycleBillingTerms.cycleLength),
            paymentPeriod: Duration.fromISO(input.cycleBillingTerms.paymentPeriod),
            cycleInvoiceDay: input.cycleBillingTerms.cycleInvoiceDay,
            isDayOfMonth: input.cycleBillingTerms.isDayOfMonth,
          }
        : undefined,
      adminId: input.adminId,
      admin: input.admin ? AdminParsers.toAdmin(input.admin) : undefined,
      statusHistory: input.statusHistory ? input.statusHistory.map(toOrderStatusHistoryJson) : [],
      googleShippingCalculation: input.googleShippingCalculation,
      returnDate: input.returnDate ? DateTime.fromISO(input.returnDate, { setZone: true }) : undefined,
      authorizedRepresentative: input.authorizedRepresentative
        ? RepresentativeParsers.toRepresentative(input.authorizedRepresentative)
        : undefined,
      defaultInvoiceFreeText: input.defaultInvoiceFreeText,
      rentalDisclaimer: input.rentalDisclaimer,
      salesRep: input.salesRep ? AdminParsers.toAdmin(input.salesRep) : undefined,
      taxBreakdown: input.taxBreakdown?.map(toTaxBreakdown),
      documentType: input.documentType,
      dispatches: input.dispatches ? input.dispatches.map(toDispatch) : undefined,
    };
  }

  export class OrderProductAdditionalFeeJson {
    id!: number;
    orderId!: number;
    productId!: number;
    orderProductId!: number;
    attributeId!: number;
    feeTitle!: string;
    feeType!: string;
    feeValue?: number;
    capValue?: number;
    feeAppliedValue!: number;
    quantity!: number;
    taxApplied!: boolean;
    recurring!: boolean;
    updatedAt!: string;
  }

  export function toOrderProductFees(input: OrderProductAdditionalFeeJson): OrderProductAdditionalFee {
    return {
      id: input.id,
      orderId: input.orderId,
      productId: input.productId,
      orderProductId: input.orderProductId,
      attributeId: input.attributeId,
      feeTitle: input.feeTitle,
      feeType: input.feeType,
      feeValue: input.feeValue,
      capValue: input.capValue,
      feeAppliedValue: input.feeAppliedValue,
      quantity: input.quantity,
      taxApplied: input.taxApplied,
      recurring: input.recurring || false,
      updatedAt: input.updatedAt ? DateTime.fromISO(input.updatedAt, { setZone: true }) : undefined,
    };
  }
  export class OrderAdditionalFeeJson {
    id!: number;
    feeId!: number;
    orderId!: number;
    feeTitle!: string;
    feeType!: string;
    feeValue!: number;
    capValue?: number;
    feeAppliedValue!: number;
    recurring!: boolean;
    taxApplied!: boolean;
    updatedAt!: string;
    optional?: boolean;
  }
  export function toOrderAdditionalFees(input: OrderAdditionalFeeJson): OrderAdditionalFee {
    return {
      id: input.id,
      feeId: input.feeId,
      orderId: input.orderId,
      feeTitle: input.feeTitle,
      feeType: input.feeType,
      feeValue: input.feeValue,
      capValue: input.capValue,
      feeAppliedValue: input.feeAppliedValue,
      recurring: input.recurring,
      taxApplied: input.taxApplied,
      updatedAt: input.updatedAt ? DateTime.fromISO(input.updatedAt, { setZone: true }) : undefined,
    };
  }
  export class OrderInternalNotesJson {
    id!: number;
    orderId!: number;
    author!: AdminParsers.AdminJson;
    comment!: string;
    createdAt!: string;
  }
  export function toOrderInternalNotes(input: OrderInternalNotesJson): OrderInternalNote {
    return {
      id: input.id,
      orderId: input.orderId,
      author: AdminParsers.toAdmin(input.author),
      comment: input.comment,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
    };
  }

  export class OrderStatusHistoryJson {
    orderId!: number;
    status!: OrderStatus;
    comments!: string;
    timestamp!: string;
    addedBy?: AdminParsers.AdminJson;
  }

  export function toOrderStatusHistoryJson(input: OrderStatusHistoryJson): OrderStatusHistory {
    return {
      orderId: input.orderId,
      status: input.status,
      comments: input.comments,
      timestamp: DateTime.fromISO(input.timestamp, { setZone: true }),
      addedBy: input.addedBy ? AdminParsers.toAdmin(input.addedBy) : undefined,
    };
  }

  export class OrderMediaJson {
    id!: string;
    orderId!: number;
    filename!: string;
    type!: OrderMediaType;
    uploadedBy!: number;
    uploadedAt!: string;
    downloadUrl?: string;
  }

  export function toOrderMedia(input: OrderMediaJson): OrderMedia {
    return {
      id: input.id,
      orderId: input.orderId,
      filename: input.filename,
      type: input.type,
      uploadedBy: input.uploadedBy,
      uploadedAt: DateTime.fromISO(input.uploadedAt, { setZone: true }),
      downloadUrl: input.downloadUrl,
    };
  }

  export class DispatchJson {
    orderId!: number;
    orderNumber!: string;
    startTime!: string;
    locationId!: number;
    timezone?: string;
    scheduledTime?: string;
    products!: { delivery?: OrderProductJson[]; pickup?: OrderProductJson[] };
    driver?: Admin;
    address?: UserAddress;
    user!: User;
    representative?: RepresentativeParsers.RepresentativeJson;
    status?: DispatchProgress;
  }

  export function toDispatch(input: DispatchJson): Dispatch {
    return {
      orderId: input.orderId,
      orderNumber: input.orderNumber,
      startTime: DateTime.fromISO(input.startTime, { setZone: true }),
      locationId: input.locationId,
      timezone: input.timezone,
      scheduledTime: input.scheduledTime ? DateTime.fromISO(input.scheduledTime, { setZone: true }) : undefined,
      products: {
        delivery: input.products.delivery
          ? input.products.delivery.map((p) => toOrderProduct(p, input.timezone))
          : undefined,
        pickup: input.products.pickup ? input.products.pickup.map((p) => toOrderProduct(p, input.timezone)) : undefined,
      },
      driver: input.driver,
      address: input.address,
      user: input.user,
      representative: input.representative ? RepresentativeParsers.toRepresentative(input.representative) : undefined,
      status: input.status,
    };
  }
}
